import { useCallback, useEffect, useRef, useState } from 'react'
import FeedbackService, {
  FeedbackCampaign,
} from '../api/common/feedback.service'
import { useNotification } from './useNotification'

const FALLBACK_SNOOZE_MS = 15 * 60 * 1000

export function useFeedbackCampaign(enabled: boolean) {
  const [campaign, setCampaign] = useState<FeedbackCampaign | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isActing, setIsActing] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()
  const loadCampaignRef = useRef<() => void>(() => undefined)
  const loadedCampaignRef = useRef<number | null>(null)
  const hiddenCampaignRef = useRef<number | null>(null)
  const { errorNotification } = useNotification()

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = undefined
    }
  }, [])

  const scheduleRefresh = useCallback(
    (nextCheckAt?: string | null) => {
      clearTimer()
      if (!nextCheckAt) {
        return
      }

      const delay = Math.max(0, new Date(nextCheckAt).getTime() - Date.now())
      timerRef.current = setTimeout(() => {
        hiddenCampaignRef.current = null
        loadedCampaignRef.current = null
        loadCampaignRef.current()
      }, delay)
    },
    [clearTimer]
  )

  const loadCampaign = useCallback(async () => {
    if (!enabled) {
      return
    }

    setIsLoading(true)
    try {
      const result = await FeedbackService.getCurrent()
      const nextCampaign = result.campaign
      setCampaign(
        nextCampaign && nextCampaign.id !== hiddenCampaignRef.current
          ? nextCampaign
          : null
      )
      scheduleRefresh(result.next_check_at)

      if (
        nextCampaign &&
        nextCampaign.id !== hiddenCampaignRef.current &&
        loadedCampaignRef.current !== nextCampaign.id
      ) {
        loadedCampaignRef.current = nextCampaign.id
        await FeedbackService.recordAction(nextCampaign.id, 'shown')
      }
    } catch {
      setCampaign(null)
      errorNotification('Feedback survey is temporarily unavailable.')
    } finally {
      setIsLoading(false)
    }
  }, [enabled, errorNotification, scheduleRefresh])

  loadCampaignRef.current = () => {
    loadCampaign()
  }

  useEffect(() => {
    if (!enabled) {
      clearTimer()
      setCampaign(null)
      return undefined
    }

    loadCampaign()
    return clearTimer
  }, [clearTimer, enabled, loadCampaign])

  const recordAction = useCallback(
    async (action: 'opened' | 'snoozed' | 'completed') => {
      if (!campaign) {
        return false
      }

      setIsActing(true)
      try {
        const state = await FeedbackService.recordAction(campaign.id, action)
        hiddenCampaignRef.current = campaign.id
        setCampaign(null)

        if (action === 'snoozed') {
          loadedCampaignRef.current = null
          const snoozeUntil = state.snoozed_until
            ? new Date(state.snoozed_until).getTime()
            : Date.now() + FALLBACK_SNOOZE_MS
          scheduleRefresh(new Date(snoozeUntil).toISOString())
        }
        return true
      } catch {
        errorNotification(
          'We could not save your survey choice. Please try again.'
        )
        return false
      } finally {
        setIsActing(false)
      }
    },
    [campaign, errorNotification, scheduleRefresh]
  )

  const takeSurvey = useCallback(async () => {
    if (!campaign) {
      return false
    }

    window.open(campaign.survey_url, '_blank', 'noopener,noreferrer')
    return recordAction('opened')
  }, [campaign, recordAction])

  const completeSurvey = useCallback(
    () => recordAction('completed'),
    [recordAction]
  )

  const snoozeSurvey = useCallback(
    () => recordAction('snoozed'),
    [recordAction]
  )

  return {
    campaign,
    isLoading,
    isActing,
    takeSurvey,
    completeSurvey,
    snoozeSurvey,
  }
}
